/**
 * Last 24H Metrics - Display total volume by chemical for the last 24 hours
 */
import { useMemo } from 'react';
import { Grid, Paper, Text, Stack } from '@mantine/core';
import type { DosingEvent } from '@/types/dosing';

// Helper to check if event is user-editable (manual entries and task-created entries)
const isManualEntry = (event: DosingEvent): boolean => {
  return event.source === 'manual' || event.source === 'task' || event.pump_number === 0;
};

interface Last24HMetricsProps {
  events: DosingEvent[];
}

export function Last24HMetrics({ events }: Last24HMetricsProps) {
  // Calculate totals per chemical for last 24h (both pump and manual)
  const metrics = useMemo(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // Filter events from last 24h, excluding future-timestamped and excluded events
    const recentEvents = events.filter((event) => {
      if (event.excluded) return false;
      const eventDate = new Date(event.timestamp);
      return eventDate > oneDayAgo && eventDate <= now;
    });

    // Track by chemical name (server-resolved for all events)
    const chemicalTotals: Record<string, { volume: number; isManual: boolean }> = {};

    recentEvents.forEach((event) => {
      const volume = event.volume_ml || 0;
      const chemical = event.chemical_name || 'Unknown';
      const isManualFlag = isManualEntry(event);

      const existing = chemicalTotals[chemical];
      if (existing) {
        existing.volume += volume;
        // Mark as manual only if all events for this chemical are manual
        existing.isManual = existing.isManual && isManualFlag;
      } else {
        chemicalTotals[chemical] = { volume, isManual: isManualFlag };
      }
    });

    // Convert to sorted array for display
    return Object.entries(chemicalTotals)
      .map(([chemical, data]) => ({
        chemical,
        volume: data.volume,
        isManual: data.isManual,
      }))
      .sort((a, b) => {
        // Pump chemicals first, then manual
        if (a.isManual !== b.isManual) return a.isManual ? 1 : -1;
        return a.chemical.localeCompare(b.chemical);
      });
  }, [events]);

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Text size="lg" fw={600}>
          Last 24 Hours
        </Text>
        <Grid>
          {metrics.map((metric) => (
            <Grid.Col
              span={{ base: 6, sm: 2.4 }}
              key={metric.chemical}
            >
              <Paper
                withBorder
                p="xs"
                style={{
                  backgroundColor:
                    metric.volume > 0
                      ? metric.isManual
                        ? 'var(--mantine-color-grape-0)'
                        : 'var(--mantine-color-blue-0)'
                      : undefined,
                }}
              >
                <Stack gap={2} align="center">
                  {metric.isManual && (
                    <Text size="xs" c="dimmed">Manual</Text>
                  )}
                  <Text size="sm" fw={500}>
                    {metric.chemical}
                  </Text>
                  <Text size="lg" fw={700}>
                    {metric.volume.toFixed(1)} mL
                  </Text>
                </Stack>
              </Paper>
            </Grid.Col>
          ))}
        </Grid>
      </Stack>
    </Paper>
  );
}
